/**
 * Persistence for a live FPS Arena match the player can reconnect into.
 *
 * The FPS runs in an iframe (apps/fps), so its Colyseus reconnection token
 * is produced inside the iframe and posted up to the web app (the
 * "hupper-fps-active" message). We store it here so a floating
 * FPSReconnectCard can offer a rejoin after a reload / navigation, and so
 * the relaunched iframe can hand the token back to `client.reconnect`.
 *
 * Lifecycle:
 *   • iframe posts "hupper-fps-active" (on join + every reconnect) → save().
 *   • FPSGameEmbed touch()es the record every ~15s while mounted so a late
 *     drop in a long match still leaves a fresh, actionable record.
 *   • match_end / consented exit / lock-failed → clear().
 *   • Short TTL — the token is only useful within the server's ≤90s
 *     reconnect grace after a drop — auto-expires a stale blob.
 */

export interface PendingFpsMatch {
  roomId: string;
  reconnectionToken: string;
  stake: string;
  walletAddress: string | null;
  /** Wall-clock of the last save / touch. */
  savedAtMs: number;
}

const STORAGE_KEY = "hupper.fps.reconnect.v1";
/** A reload during the server's ≤90s grace must still find the record;
 *  keep a small buffer past that, then let it expire. */
const TTL_MS = 150_000;

function isExpired(p: PendingFpsMatch): boolean {
  return Date.now() - p.savedAtMs > TTL_MS;
}

export function saveFpsPending(p: PendingFpsMatch): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/** Refresh `savedAtMs` on the existing record without other changes. */
export function touchFpsPending(): void {
  const cur = loadFpsPending();
  if (cur) saveFpsPending({ ...cur, savedAtMs: Date.now() });
}

export function loadFpsPending(): PendingFpsMatch | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingFpsMatch;
    if (!parsed?.roomId || !parsed?.reconnectionToken) return null;
    if (isExpired(parsed)) {
      clearFpsPending();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearFpsPending(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
